import { Control, type ControlOptions } from "../core/Control.js";
import { CARET_ROOM, stepIndex, wordAt, wordBoundary } from "./textEditing.js";
import type { KeyEventArgs, MouseEventArgs, TextInputArgs } from "../core/events.js";
import { clamp, rectDeflate, type Rect, type Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface TextBoxOptions extends ControlOptions {
  text?: string;
  font?: FontSpec;
  placeholder?: string;
  readOnly?: boolean;
  maxLength?: number;
  /** Render every character as this glyph, e.g. "•" for a password field. */
  passwordChar?: string;
  /**
   * Draw the field's own frame. Set false when the field sits inside chrome
   * that already provides one, such as an editable ComboBox, to avoid a
   * border inside a border.
   */
  frame?: boolean;
  onChange?: () => void;
}

const CARET_BLINK_MS = 530;

/** Newlines and tabs collapse to a single space in a one-line field. */
const NEWLINES = new RegExp("[\r\n\t]", "g");
/** C0 and DEL control characters are dropped outright. */
const CONTROL_CHARS = new RegExp("[\u0000-\u001f\u007f]", "g");

/**
 * A single-line editable text field.
 *
 * Editing state is a caret index plus a selection anchor, both counted in
 * UTF-16 code units so they line up with the hidden textarea the clipboard and
 * IME go through.
 */
export class TextBox extends Control {
  #text: string;
  #font: FontSpec | null;
  #caret = 0;
  #anchor = 0;
  /** Horizontal scroll of the text inside the field, logical px. */
  #scroll = 0;
  #caretOn = true;
  #blinkTimer = 0;
  /** In-progress IME text, shown at the caret but not yet committed. */
  #composition = "";
  #dragging = false;
  /** Set while a double-click drag extends the selection by whole words. */
  #wordDrag: { start: number; end: number } | null = null;

  placeholder: string;
  readOnly: boolean;
  maxLength: number;
  passwordChar: string | null;
  /** Whether to paint the field's own border. */
  frame: boolean;

  constructor(options: TextBoxOptions = {}) {
    super({ width: 180, height: 30, tabStop: true, cursor: "text", ...options });
    this.#text = options.text ?? "";
    this.#font = options.font ?? null;
    this.placeholder = options.placeholder ?? "";
    this.readOnly = options.readOnly ?? false;
    this.maxLength = options.maxLength ?? Number.MAX_SAFE_INTEGER;
    this.passwordChar = options.passwordChar ?? null;
    this.frame = options.frame ?? true;
    this.#caret = this.#anchor = this.#text.length;
    if (options.onChange) this.on("change", options.onChange);

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", () => this.#onPointerUp());
    this.on("doubleclick", (e) => this.#onDoubleClick(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
    this.on("textinput", (e) => this.#onTextInput(e));
    this.on("focus", () => this.#onFocus());
    this.on("blur", () => this.#onBlur());
  }

  // --- value ------------------------------------------------------------

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    const next = v.slice(0, this.maxLength);
    if (this.#text === next) return;
    this.#text = next;
    this.#caret = this.#anchor = Math.min(this.#caret, next.length);
    this.#ensureCaretVisible();
    this.emit("textchanged");
    this.emit("change");
    this.#syncMirror();
    this.invalidate();
  }

  get font(): FontSpec | null {
    return this.#font;
  }
  set font(v: FontSpec | null) {
    this.#font = v;
    this.invalidate();
  }

  override get acceptsTextInput(): boolean {
    return true;
  }

  get caretIndex(): number {
    return this.#caret;
  }

  get selectionStart(): number {
    return Math.min(this.#anchor, this.#caret);
  }

  get selectionEnd(): number {
    return Math.max(this.#anchor, this.#caret);
  }

  get selectedText(): string {
    return this.#text.slice(this.selectionStart, this.selectionEnd);
  }

  get hasSelection(): boolean {
    return this.#anchor !== this.#caret;
  }

  select(start: number, end = start): void {
    const n = this.#text.length;
    this.#anchor = clamp(start, 0, n);
    this.#caret = clamp(end, 0, n);
    this.#restartBlink();
    this.#ensureCaretVisible();
    this.#syncMirror();
    this.invalidate();
  }

  selectAll(): void {
    this.select(0, this.#text.length);
  }

  // --- rendering helpers --------------------------------------------------

  /** What is actually painted — the password mask, if any. */
  #displayText(source = this.#text): string {
    if (!this.passwordChar) return source;
    return this.passwordChar.repeat([...source].length);
  }

  #themeFont(theme: Theme): FontSpec {
    return this.#font ?? theme.metrics.font;
  }

  /** Text area inside the field's border and padding, in local coords. */
  #contentRect(theme: Theme): Rect {
    return rectDeflate(this.localRect, theme.metrics.textBoxPadding);
  }

  #measureTo(index: number): number {
    const desktop = this.desktop;
    if (!desktop) return 0;
    const font = this.#themeFont(desktop.theme);
    return desktop.rc.measureText(this.#displayText(this.#text.slice(0, index)), font).width;
  }

  /** Caret index nearest a local x coordinate. */
  #indexAtX(localX: number): number {
    const desktop = this.desktop;
    if (!desktop) return 0;
    const theme = desktop.theme;
    const content = this.#contentRect(theme);
    const x = localX - content.x + this.#scroll;
    const font = this.#themeFont(theme);
    const index = desktop.rc.text.indexAtX(
      this.#displayText(),
      font,
      desktop.gpu.size.dpr,
      Math.max(0, x),
    );
    // Password masking can change code-unit counts; clamp to the real text.
    return clamp(index, 0, this.#text.length);
  }

  /**
   * Scroll the least that leaves the caret somewhere it can be drawn.
   *
   * `CARET_ROOM` is why the end of the text is reachable at all. The field clips its painting
   * to `content`, and a caret at the end of a longer-than-the-field string sits exactly on that
   * rect's right edge, so a one-pixel caret falls entirely outside the clip: the caret is there,
   * and nothing is drawn. Both the test and the limit below reserve the same room, because a
   * limit that stopped at the text's own width would refuse the scroll the test just asked for.
   */
  #ensureCaretVisible(): void {
    const desktop = this.desktop;
    if (!desktop) return;
    const content = this.#contentRect(desktop.theme);
    if (content.width <= 0) return;
    const caretX = this.#measureTo(this.#caret + this.#composition.length);
    const total = this.#measureTo(this.#text.length) + this.#compositionWidth();

    if (caretX - this.#scroll < 0) this.#scroll = caretX;
    else if (caretX + CARET_ROOM - this.#scroll > content.width) {
      this.#scroll = caretX + CARET_ROOM - content.width;
    }
    // Never scroll past the end, and never leave a gap when the text fits.
    this.#scroll = clamp(this.#scroll, 0, Math.max(0, total + CARET_ROOM - content.width));
  }

  #compositionWidth(): number {
    if (!this.#composition) return 0;
    const desktop = this.desktop;
    if (!desktop) return 0;
    return desktop.rc.measureText(this.#composition, this.#themeFont(desktop.theme)).width;
  }

  // --- editing ------------------------------------------------------------

  /** Replace the selection (or insert at the caret) with `insert`. */
  #replaceSelection(insert: string): void {
    if (this.readOnly) return;
    const start = this.selectionStart;
    const end = this.selectionEnd;
    const room = this.maxLength - (this.#text.length - (end - start));
    const clipped = insert.slice(0, Math.max(0, room));
    if (clipped.length === 0 && start === end) return;
    this.#text = this.#text.slice(0, start) + clipped + this.#text.slice(end);
    this.#caret = this.#anchor = start + clipped.length;
    this.#restartBlink();
    this.#ensureCaretVisible();
    this.emit("textchanged");
    this.emit("change");
    this.#syncMirror();
    this.invalidate();
  }

  #deleteSelection(): boolean {
    if (!this.hasSelection || this.readOnly) return false;
    this.#replaceSelection("");
    return true;
  }

  #moveCaret(index: number, extend: boolean): void {
    const n = this.#text.length;
    this.#caret = clamp(index, 0, n);
    if (!extend) this.#anchor = this.#caret;
    this.#restartBlink();
    this.#ensureCaretVisible();
    this.#syncMirror();
    this.invalidate();
  }

  // --- input handlers ------------------------------------------------------

  #onPointerDown(e: MouseEventArgs): void {
    if (e.button !== 0) return;
    this.focus();
    if (e.clickCount >= 3) {
      this.selectAll();
      e.handled = true;
      return;
    }
    const index = this.#indexAtX(e.x);
    this.#dragging = true;
    this.#wordDrag = null;
    this.desktop?.input.setCapture(this);
    if (e.shiftKey) this.#moveCaret(index, true);
    else this.select(index);
    e.handled = true;
  }

  #onPointerMove(e: MouseEventArgs): void {
    if (!this.#dragging) return;
    const index = this.#indexAtX(e.x);
    if (this.#wordDrag) {
      // Once a word is selected, dragging extends by whole words.
      const w = wordAt(this.#text, index);
      if (index < this.#wordDrag.start) this.select(this.#wordDrag.end, w.start);
      else this.select(this.#wordDrag.start, w.end);
      return;
    }
    this.#moveCaret(index, true);
  }

  #onPointerUp(): void {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.#wordDrag = null;
    this.desktop?.input.releaseCapture(this);
  }

  #onDoubleClick(e: MouseEventArgs): void {
    const w = wordAt(this.#text, this.#indexAtX(e.x));
    this.#wordDrag = w;
    this.select(w.start, w.end);
    e.handled = true;
  }

  #onKeyDown(e: KeyEventArgs): void {
    const ctrl = e.ctrlKey || e.metaKey;
    const extend = e.shiftKey;

    switch (e.key) {
      case "ArrowLeft":
        this.#moveCaret(
          ctrl ? wordBoundary(this.#text, this.#caret, -1) : this.#collapseOr(-1, extend),
          extend,
        );
        break;
      case "ArrowRight":
        this.#moveCaret(
          ctrl ? wordBoundary(this.#text, this.#caret, 1) : this.#collapseOr(1, extend),
          extend,
        );
        break;
      case "Home":
        this.#moveCaret(0, extend);
        break;
      case "End":
        this.#moveCaret(this.#text.length, extend);
        break;
      case "Backspace": {
        if (this.readOnly) break;
        if (this.#deleteSelection()) break;
        const from = ctrl ? wordBoundary(this.#text, this.#caret, -1) : stepIndex(this.#text, this.#caret, -1);
        if (from === this.#caret) break;
        this.select(from, this.#caret);
        this.#replaceSelection("");
        break;
      }
      case "Delete": {
        if (this.readOnly) break;
        if (this.#deleteSelection()) break;
        const to = ctrl ? wordBoundary(this.#text, this.#caret, 1) : stepIndex(this.#text, this.#caret, 1);
        if (to === this.#caret) break;
        this.select(this.#caret, to);
        this.#replaceSelection("");
        break;
      }
      case "a":
      case "A":
        if (!ctrl) return; // an ordinary "a" is delivered as text input
        this.selectAll();
        break;
      case "Escape":
      case "Tab":
        return; // let the desktop handle traversal and dialog keys
      default:
        // Let the browser's own copy/cut/paste events run.
        return;
    }
    e.handled = true;
  }

  /** Arrow keys collapse an existing selection instead of moving the caret. */
  #collapseOr(direction: 1 | -1, extend: boolean): number {
    if (!extend && this.hasSelection) {
      return direction > 0 ? this.selectionEnd : this.selectionStart;
    }
    return stepIndex(this.#text, this.#caret, direction);
  }

  #onTextInput(e: TextInputArgs): void {
    if (this.readOnly) return;
    // A single-line field takes no newlines, and no control characters.
    const text = e.text.replace(NEWLINES, " ").replace(CONTROL_CHARS, "");
    if (!text) return;
    this.#replaceSelection(text);
    e.handled = true;
  }

  /**
   * Called by the Desktop when the hidden field's text changed: replace
   * [start, end) with `text`. This is how a phone keyboard edits, since it
   * rewrites words in place rather than sending keystrokes.
   */
  replaceTextRange(start: number, end: number, text: string): void {
    if (this.readOnly) return;
    const length = this.#text.length;
    const from = Math.max(0, Math.min(start, length));
    const to = Math.max(from, Math.min(end, length));
    this.select(from, to);
    this.#replaceSelection(text.replace(NEWLINES, " ").replace(CONTROL_CHARS, ""));
    // Whatever the control refused (a newline, past maxLength) has to leave
    // the field too, or the next edit's offsets are off by it.
    this.#syncMirror();
  }

  /** Called by the Desktop for native copy/cut. */
  getClipboardText(cut: boolean): string | null {
    if (!this.hasSelection) return null;
    const text = this.selectedText;
    if (this.passwordChar) return null; // never leak a masked value
    if (cut && !this.readOnly) this.#replaceSelection("");
    return text;
  }

  /** Called by the Desktop while an IME composition is in progress. */
  setComposition(text: string): void {
    if (this.#composition === text) return;
    this.#composition = text;
    this.#ensureCaretVisible();
    this.invalidate();
  }

  #onFocus(): void {
    this.#restartBlink();
    this.#syncMirror();
    this.invalidate();
  }

  #onBlur(): void {
    this.#stopBlink();
    this.#composition = "";
    this.#dragging = false;
    this.invalidate();
  }

  #restartBlink(): void {
    this.#caretOn = true;
    this.#stopBlink();
    this.#blinkTimer = setInterval(() => {
      this.#caretOn = !this.#caretOn;
      this.invalidate();
    }, CARET_BLINK_MS);
  }

  #stopBlink(): void {
    if (this.#blinkTimer !== 0) clearInterval(this.#blinkTimer);
    this.#blinkTimer = 0;
  }

  /** Keep the hidden textarea in step so clipboard and IME see our state. */
  #syncMirror(): void {
    const desktop = this.desktop;
    if (!desktop || desktop.focusManager.focused !== this) return;
    desktop.keyboard.setMirroredText(this.#text, this.selectionStart, this.selectionEnd);
    const caretLocal = this.#caretLocalX();
    if (caretLocal !== null) {
      desktop.keyboard.setCaretPosition(this.toScreen({ x: caretLocal, y: this.height }));
    }
  }

  #caretLocalX(): number | null {
    const desktop = this.desktop;
    if (!desktop) return null;
    const content = this.#contentRect(desktop.theme);
    return content.x + this.#measureTo(this.#caret) - this.#scroll;
  }

  // --- painting -------------------------------------------------------------


  /**
   * One theme control-height tall; the width stays whatever it was given.
   *
   * A field has no content width worth measuring — it is as wide as the form wants it, not as
   * wide as the text someone happens to have typed — so the only interesting axis is the one the
   * theme decides.
   */
  override preferredSize(_available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    return { width: this.width, height: desktop.theme.metrics.controlHeight };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    const p = theme.palette;
    if (this.frame) theme.drawTextBox(rc, this.localRect, state);

    const content = this.#contentRect(theme);
    if (content.width <= 0 || content.height <= 0) return;
    const font = this.#themeFont(theme);
    const midY = content.y + content.height / 2;

    if (!this.#text && !this.#composition) {
      if (this.placeholder) {
        rc.clipped(content, () => {
          rc.drawText(this.placeholder, content.x, midY, font, p.placeholderText, {
            baseline: "middle",
          });
        });
      }
      if (this.focused) this.#paintCaret(rc, theme, content, content.x);
      return;
    }

    rc.clipped(content, () => {
      const baseX = content.x - this.#scroll;
      const display = this.#displayText();
      const selStart = this.selectionStart;
      const selEnd = this.selectionEnd;
      const ink = state.disabled ? p.textDisabled : p.fieldText;

      if (this.hasSelection && this.focused) {
        const x0 = this.#measureTo(selStart);
        const x1 = this.#measureTo(selEnd);
        rc.fillRect(
          { x: baseX + x0, y: content.y, width: x1 - x0, height: content.height },
          p.textSelectionBg,
        );
        // Three runs so the selected span can use the selection ink color.
        rc.drawText(display.slice(0, selStart), baseX, midY, font, ink, { baseline: "middle" });
        rc.drawText(display.slice(selStart, selEnd), baseX + x0, midY, font, p.selectionText, {
          baseline: "middle",
        });
        rc.drawText(display.slice(selEnd), baseX + x1, midY, font, ink, { baseline: "middle" });
      } else {
        rc.drawText(display, baseX, midY, font, ink, { baseline: "middle" });
      }

      let caretX = baseX + this.#measureTo(this.#caret);
      if (this.#composition) {
        const w = this.#compositionWidth();
        // Draw the pending IME text over whatever follows, with an underline.
        rc.fillRect({ x: caretX, y: content.y, width: w, height: content.height }, p.fieldBg);
        rc.drawText(this.#composition, caretX, midY, font, ink, { baseline: "middle" });
        rc.hLine(caretX, content.y + content.height - 1, w, ink);
        caretX += w;
      }
      if (this.focused) this.#paintCaret(rc, theme, content, caretX);
    });
  }

  #paintCaret(rc: RenderContext, theme: Theme, content: Rect, x: number): void {
    if (!this.#caretOn || this.readOnly || !this.effectivelyEnabled) return;
    rc.fillRect(
      { x: Math.round(x), y: content.y, width: 1, height: content.height },
      theme.palette.fieldText,
    );
  }
}
